"use client";

import React, { useRef, useState, MouseEvent } from "react";
import { User, Megaphone, TrendingUp } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
const features = [{
  title: "Club President",
  description: "Leading the vision and strategic direction of WT Birkalla. For executive matters and club governance inquiries, contact our president directly.",
  icon: User
}, {
  title: "Club Secretary",
  description: "Managing club operations, registrations, and official correspondence. Reach out for administrative support and general club information.",
  icon: Megaphone
}, {
  title: "Head Registrar",
  description: "Overseeing player enrollments across all divisions. Contact the registrar for fees, player transfers, and team placement details.",
  icon: TrendingUp
}];
function TiltCard({
  feature,
  index
}: {
  feature: (typeof features)[0];
  index: number;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const [isHovered, setIsHovered] = useState(false);
  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const centerX = rect.width / 2;
    const centerY = rect.height / 2;
    const rotateX = (y - centerY) / centerY * -15;
    const rotateY = (x - centerX) / centerX * 15;
    cardRef.current.style.transform = `perspective(1000px) rotateX(${rotateX}deg) rotateY(${rotateY}deg) scale3d(1.02, 1.02, 1.02)`;
  };
  const handleMouseLeave = () => {
    setIsHovered(false);
    if (!cardRef.current) return;
    cardRef.current.style.transform = `perspective(1000px) rotateX(0deg) rotateY(0deg) scale3d(1, 1, 1)`;
  };
  const handleMouseEnter = () => {
    setIsHovered(true);
  };
  const Icon = feature.icon;
  return <div data-index={index} className="relative group h-full w-full" style={{
    perspective: "1000px"
  }}>
      {}
      <div ref={cardRef} onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave} onMouseEnter={handleMouseEnter} className="h-full w-full rounded-none bg-gradient-to-br from-primary/40 via-primary/10 to-transparent p-[1px] transition-all duration-200 ease-out will-change-transform">
        <Card className="relative h-full w-full overflow-hidden border-none bg-card/80 backdrop-blur-2xl transition-colors duration-500 group-hover:bg-card">
          {}
          <div className={`absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent opacity-0 transition-opacity duration-500 ${isHovered ? "opacity-100" : ""}`} />

          <CardHeader className="relative z-10 pb-4 pt-10">
            <div className="mb-6 inline-flex h-16 w-16 items-center justify-center rounded-none bg-primary/10 shadow-[0_0_30px_rgba(var(--primary),0.2)] ring-1 ring-primary/30 transition-transform duration-500 group-hover:scale-110 group-hover:shadow-[0_0_40px_rgba(var(--primary),0.4)]">
              <Icon className="h-8 w-8 text-primary" strokeWidth={1.5} />
            </div>
            <CardTitle className="font-default text-2xl font-extrabold tracking-tighter text-primary uppercase">
              {feature.title}
            </CardTitle>
          </CardHeader>
          <CardContent className="relative z-10 pb-10">
            <CardDescription className="font-default text-base leading-relaxed text-foreground/80">
              {feature.description}
            </CardDescription>
            <div className="mt-6">
              <a href="mailto:admin@wtbirkalla.com.au" className="font-mono text-sm font-bold text-primary hover:underline tracking-wider">
                CONTACT OFFICIAL →
              </a>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}
export default function WhyThisIntensive() {
  return <section data-section-id="222770" id="key-contacts" className="relative overflow-hidden bg-background py-32 text-foreground">
      {}
      <div className="pointer-events-none absolute inset-0 -z-10">
        <div className="absolute left-1/4 top-1/4 h-[500px] w-[500px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-primary/5 mix-blend-screen blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 h-[600px] w-[600px] translate-x-1/3 translate-y-1/3 rounded-full bg-primary/5 mix-blend-screen blur-[150px]" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/stardust.png')] opacity-10 mix-blend-overlay" />
      </div>

      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-20 text-center">
          <h2 className="font-default text-4xl font-extrabold tracking-tighter sm:text-5xl md:text-6xl uppercase">
            Key{" "}
            <span className="bg-gradient-to-r from-primary to-primary/60 bg-clip-text text-transparent">
              Contacts
            </span>
          </h2>
          <p className="mx-auto mt-6 max-w-2xl font-default text-lg text-muted-foreground">
            Connect with the leadership team driving WT Birkalla forward. We are committed to transparency and community development.
          </p>
        </div>

        <div className="mx-auto grid max-w-6xl grid-cols-1 gap-8 md:grid-cols-3">
          {features.map((feature, index) => <TiltCard key={index} feature={feature} index={index} />)}
        </div>
      </div>
    </section>;
}